import { Alert, Button, Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdArrowBack } from 'react-icons/md'
import { TbFolderCog } from 'react-icons/tb'
import { Link, useParams } from 'react-router-dom'

import { withDiskFeature, diskErrorText, formatBytes } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { Panel } from '@/Ship/Components/Dashboard/Overview/shared'
import { classNames } from '@/Ship/Helpers/classNames'

import { filesLabel } from '../Components/DiskAdminCharts'
import { DiskAdminShell } from '../Components/DiskAdminShell'
import { PersonAvatar, StorageBadges, storageSubtitle } from '../Components/StorageName'
import { formatDate } from '../Helpers/diskAdminFormat'
import { diskAdminStorageUrl, diskAdminStoragesUrl } from '../Helpers/diskAdminRoutes'
import { LoadDiskOffboardingTask } from '../Tasks/LoadDiskOffboardingsTask'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import type { DiskOffboardingRow } from '../Contracts/DiskAdmin'

import styles from '../Components/DiskAdmin.module.css'

/**
 * Справа звільнення `/disk/admin/offboarding/<uuid>` — сюди ведуть
 * сповіщення `Disk.Offboarding` і кнопка «Вирішити долю диска» картки.
 *
 * Поки що — підсумок справи (хто, строк, наступник за замовчуванням, диск) і
 * перехід у картку диска; майстер рішення з групами й наслідками (наряд Ф6)
 * ляже на цю ж адресу.
 */
const OffboardingPage = observer(() => {
    const { uuid } = useParams()
    const [row, setRow] = useState<DiskOffboardingRow | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!uuid) return
        setRow(null)
        setError(null)
        LoadDiskOffboardingTask(uuid)
            .then(setRow)
            .catch((failure: unknown) => setError(diskErrorText(failure, 'Не вдалося відкрити справу звільнення')))
    }, [uuid])

    return (
        <DiskAdminShell tab="storages" documentTitle="Справа звільнення — адміністрування диска">
            <Link to={diskAdminStoragesUrl('departed')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <MdArrowBack /> Звільнені
            </Link>
            {error !== null ? <Alert type="error" showIcon title={error} /> : null}
            {row === null && error === null ? (
                <div style={{ padding: 48, textAlign: 'center' }}>
                    <Spin />
                </div>
            ) : null}
            {row !== null ? (
                <>
                    <section className={classNames(styles['hero'])} data-block="disk-admin-offboarding">
                        <div className={classNames(styles['heroMain'])}>
                            {row.user ? <PersonAvatar person={row.user} size={56} /> : null}
                            <div style={{ minWidth: 0 }}>
                                <h2 className={classNames(styles['heroTitle'])}>Диск {row.user?.name ?? row.storage?.title ?? ''}</h2>
                                <div className={classNames(styles['heroMeta'])}>
                                    <span>{storageSubtitle('personal', row.user)}</span>
                                    {row.storage ? <StorageBadges state={row.storage.state} departed hasDisk /> : null}
                                </div>
                            </div>
                        </div>
                        {row.storage ? (
                            <div className={classNames(styles['heroActions'])}>
                                <Link to={diskAdminStorageUrl(row.storage.uuid)}>
                                    <Button type="primary">Картка диска</Button>
                                </Link>
                            </div>
                        ) : null}
                    </section>
                    <Alert
                        type={row.overdue ? 'error' : 'warning'}
                        showIcon
                        title={`${row.statusLabel}. Деактивовано ${formatDate(row.deactivatedAt)}, рішення до ${formatDate(row.dueAt)}${
                            row.daysLeft !== null ? (row.daysLeft < 0 ? ` — прострочено на ${-row.daysLeft} дн.` : ` — лишилось ${row.daysLeft} дн.`) : ''
                        }`}
                    />
                    {row.storage ? (
                        <Panel block="disk-admin-offboarding-storage" title="Що на диску">
                            <div className={classNames(styles['reclaim'])}>
                                <div className={classNames(styles['reclaimRow'])}>
                                    <span className={classNames(styles['reclaimTitle'])}>Зайнято</span>
                                    <span className={classNames(styles['reclaimValue'])}>{formatBytes(row.storage.used)}</span>
                                    <span className={classNames(styles['reclaimNote'])}>{filesLabel(row.storage.files)}</span>
                                </div>
                            </div>
                        </Panel>
                    ) : null}
                </>
            ) : null}
        </DiskAdminShell>
    )
})

export const pageConfig: IPageConfig = {
    key: 'Disk.AdminOffboarding',
    route: '/disk/admin/offboarding/:uuid',
    title: 'Справа звільнення — адміністрування диска',
    icon: <TbFolderCog />,
}

export default withDiskFeature(OffboardingPage)
